<template>
  <div class="pos-rail">
    <div class="rail-block customer-block">
      <div class="rail-head"><i class="fa fa-user mr-1"></i> Customer</div>
      <div class="d-flex gap-1 mb-1">
        <input
          ref="mobileField"
          v-model="mobileNo"
          type="text"
          class="form-control form-control-sm"
          placeholder="Mobile No"
          @keydown.enter="searchContact"
          @keydown.down.prevent="focusSerial"
          @keydown.up.prevent="focusNoteReceived"
        />
        <input
          ref="serialField"
          v-model="serialNo"
          type="text"
          class="form-control form-control-sm"
          placeholder="ID"
          @keydown.enter="searchContact"
          @keydown.down.prevent="focusDiscountPercent"
          @keydown.up.prevent="focusMobile"
        />
        <button type="button" class="btn btn-sm btn-outline-primary px-1" @click="openCustomerAddModal">
          <i class="fa fa-plus"></i>
        </button>
      </div>
      <AsyncSelect
        placeholder="Select Customer"
        v-model="customer"
        :api-service="fetchContactProfiles"
        :additional-query="additionalQuery"
        :format-label="formatPatientLabel"
      />
      <div class="meta-row">
        <span class="chip">ID <strong>{{ patientId }}</strong></span>
        <span class="chip">Phone <strong>{{ patientMobile }}</strong></span>
        <span class="chip chip-points"><i class="fa fa-star"></i> <strong>{{ rewardBalance }}</strong> pts</span>
      </div>
    </div>

    <div class="rail-block">
      <div class="rail-head"><i class="fa fa-calculator mr-1"></i> Totals</div>
      <div class="amount-row">
        <span>Total Items</span>
        <span class="val">{{ totalItems }}</span>
      </div>
      <div class="amount-row">
        <span>Total Qty</span>
        <span class="val">{{ totalQuantity }}</span>
      </div>
      <div class="amount-row">
        <span>Net Bill Amount</span>
        <span class="val">{{ total }}</span>
      </div>
      <div class="amount-row">
        <span>Discount %</span>
        <input
          ref="discountPercentField"
          v-model.trim="discountPercentInput"
          type="number"
          class="form-control form-control-sm rail-input"
          placeholder="%"
          :min="0"
          @keydown.down.prevent="focusDiscountAmount"
          @keydown.up.prevent="focusSerial"
        />
      </div>
      <div class="amount-row">
        <span>Discount Amount</span>
        <input
          ref="discountAmountField"
          v-model.trim="discountAmountInput"
          type="number"
          class="form-control form-control-sm rail-input"
          placeholder="amount"
          :min="0"
          @keydown.down.prevent="focusPointsRedeemed"
          @keydown.up.prevent="focusDiscountPercent"
        />
      </div>
      <div v-if="discountAmount > 0" class="mt-1">
        <v-select
          placeholder="Select Discount Head"
          v-model="formData.discount_head_id"
          :options="salesAccountHeads"
          label="name"
          :reduce="name => name.id"
        />
      </div>
      <div class="amount-row">
        <span>Redeem Points ({{ rewardBalance }})</span>
        <input
          ref="pointsRedeemedField"
          v-model.trim="pointsRedeemedInput"
          type="number"
          class="form-control form-control-sm rail-input point-input"
          placeholder="points"
          :min="0"
          @keydown.down.prevent="focusPaidAmount"
          @keydown.up.prevent="focusDiscountAmount"
        />
      </div>
      <div v-if="formData.points_redeemed > 0" class="mt-1">
        <v-select
          placeholder="Select Reward Head"
          v-model="formData.liability_head_id"
          :options="rewardPointsAccountHeads"
          label="name"
          :reduce="name => name.id"
        />
      </div>
    </div>

    <div class="rail-block net-block" :class="`net-block--${paymentState}`">
      <div class="net-row">
        <span>Net Payable</span>
        <span class="net-val">{{ netPayable }}</span>
      </div>
      <div v-if="paymentStatusLabel" class="net-status">
        <span class="net-status-badge">{{ paymentStatusLabel }}</span>
      </div>
    </div>

    <div class="rail-block">
      <div class="rail-head"><i class="fa fa-credit-card mr-1"></i> Mode of Payment</div>
      <v-select
        placeholder="Select Cash & Bank Account"
        v-model="formData.mop_account_head_id"
        :options="cashAndBank"
        label="name"
        :reduce="name => name.id"
      />
      <div class="amount-row mt-1">
        <span>Paid Amount</span>
        <input
          ref="paidAmountField"
          v-model="paidAmountInput"
          type="number"
          class="form-control form-control-sm rail-input paid-input"
          placeholder="amount"
          :min="0"
          @input="onPaymentInput"
          @blur="validatePaymentAmount"
          @keydown.down.prevent="focusNoteReceived"
          @keydown.up.prevent="focusPointsRedeemed"
        />
      </div>
      <div class="amount-row">
        <span>Note Received</span>
        <input
          ref="noteReceivedField"
          v-model="receivedAmount"
          type="number"
          class="form-control form-control-sm rail-input"
          placeholder="amount"
          :min="0"
          @keydown.enter.exact="$emit('submit')"
          @keydown.down.prevent="focusMobile"
          @keydown.up.prevent="focusPaidAmount"
        />
      </div>
      <div class="quick-cash">
        <button type="button" class="btn quick-btn" @click="setExactCash">Exact</button>
        <button
          v-for="note in quickNotes"
          :key="note"
          type="button"
          class="btn quick-btn"
          @click="setQuickCash(note)"
        >{{ note }}</button>
      </div>
      <div class="amount-row change-row">
        <span>Change Returned</span>
        <span class="change-val">{{ changeAmount }}</span>
      </div>
    </div>

    <button class="btn btn-block submit-btn" :disabled="isSubmitting" @click="$emit('submit')">
      <i class="fa fa-print mr-1"></i>
      {{ submitLabel }}
    </button>

    <div class="rail-actions">
      <button class="btn btn-sm btn-outline-primary" @click="$emit('newBill')">
        <i class="fa fa-plus mr-1"></i> New Bill
      </button>
      <button class="btn btn-sm btn-outline-danger" @click="$emit('clear')">
        <i class="fa fa-refresh mr-1"></i> Clear
      </button>
    </div>

    <teleport to="body">
      <AddCustomerModal
        v-if="$store.state.isModalOpenTwo"
        :mobileNotReq="true"
        :serialNoReq="true"
        @onCreateProfile="onCreateProfile"
      />
      <Loader v-if="isLoader"/>
    </teleport>
  </div>
</template>

<script setup>
import { computed, inject, nextTick, onMounted, ref, toRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import AddCustomerModal from '@/components/molecule/company/inventory/AddCustomerModal.vue'
import Loader from '@/components/atom/LoaderComponent'
import handleContact from '@/services/modules/contact'
import handleCompanyContact from '@/services/modules/companyContact'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import useInvoiceCalculations from '@/composables/useInvoiceCalculations.js'

const props = defineProps({
  tableItems: {
    type: Array,
    default: () => []
  },
  salesAccountHeads: {
    type: Array,
    default: () => []
  },
  rewardPointsAccountHeads: {
    type: Array,
    default: () => []
  },
  cashAndBank: {
    type: Array,
    default: () => []
  },
  isSubmitting: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['clear', 'newBill', 'submit'])

const $store = useStore()
const $route = useRoute()
const formData = inject('formData')
const totals = inject('totals')
const showError = inject('showError')
const tableItemsRef = toRef(props, 'tableItems')

const { fetchContactProfiles, fetchSingleContactProfile } = handleContact()
const { fetchCompanyAllContacts } = handleCompanyContact()
const { formatPatientLabel } = useAsyncDropdownHelper()

const {
  total,
  grandTotal,
  discountAmount,
  discountPercent,
  resetDiscounts
} = useInvoiceCalculations(tableItemsRef, totals)

const companyId = $route.params.companyId
const companyQuery = `?company_id=${companyId}`
const customer = ref(null)
const selectedData = ref({})
const serialNo = ref(null)
const mobileNo = ref(null)
const receivedAmount = ref('')
const isLoader = ref(false)
const paymentTouched = ref(false)
const mobileField = ref(null)
const serialField = ref(null)
const discountPercentField = ref(null)
const discountAmountField = ref(null)
const pointsRedeemedField = ref(null)
const paidAmountField = ref(null)
const noteReceivedField = ref(null)
const additionalQuery = {
  roles: 'customer,employee',
  age: 1,
  gender: 1
}

const totalItems = computed(() => props.tableItems.length)

const totalQuantity = computed(() => {
  return props.tableItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
})

const rewardBalance = computed(() => {
  if (selectedData.value && selectedData.value.reward_balance) return selectedData.value.reward_balance
  return 0
})

const patientMobile = computed(() => {
  if (selectedData.value && selectedData.value.profile_items && selectedData.value.profile_items.length) {
    return selectedData.value.profile_items[0].value
  }
  return null
})

const patientId = computed(() => {
  if (
    selectedData.value &&
    selectedData.value.company_roles &&
    selectedData.value.company_roles.length &&
    selectedData.value.company_roles[0].pivot
  ) {
    return selectedData.value.company_roles[0].pivot.serial_no
  }
  return null
})

const discountPercentInput = computed({
  get() {
    if (! discountPercent.value) return ''
    return discountPercent.value
  },
  set(value) {
    discountPercent.value = value
  }
})

const discountAmountInput = computed({
  get() {
    if (! discountAmount.value) return ''
    return discountAmount.value
  },
  set(value) {
    discountAmount.value = value
  }
})

const pointsRedeemedInput = computed({
  get() {
    if (! formData.value.points_redeemed) return ''
    return formData.value.points_redeemed
  },
  set(value) {
    formData.value.points_redeemed = value
  }
})

const paidAmountInput = computed({
  get() {
    if (! formData.value.payment) return ''
    return formData.value.payment
  },
  set(value) {
    formData.value.payment = value
  }
})

const netPayable = computed(() => {
  const billTotal = Number(grandTotal.value) || 0
  const redeemed = Number(formData.value.points_redeemed) || 0
  return parseFloat((billTotal - redeemed).toFixed(4))
})

const quickNotes = [100, 500, 1000]

const setQuickCash = (note) => {
  receivedAmount.value = note
}

const setExactCash = () => {
  receivedAmount.value = Number(formData.value.payment) || 0
}

const changeAmount = computed(() => {
  const paid = Number(formData.value.payment) || 0
  const received = Number(receivedAmount.value) || 0
  if (paid <= 0 || received <= 0) return 0
  return parseFloat((received - paid).toFixed(4))
})

const dueAmount = computed(() => {
  const payable = Number(netPayable.value) || 0
  const paid = Number(formData.value.payment) || 0
  const due = parseFloat((payable - paid).toFixed(4))
  if (due <= 0) return 0
  return due
})

const paymentState = computed(() => {
  if (netPayable.value <= 0) return 'empty'
  if (dueAmount.value > 0) return 'due'
  if (changeAmount.value > 0) return 'change'
  return 'settled'
})

const paymentStatusLabel = computed(() => {
  if (paymentState.value === 'due') return `Due ${dueAmount.value}`
  if (paymentState.value === 'change') return `Change ${changeAmount.value}`
  if (paymentState.value === 'settled') return 'Fully paid'
  return ''
})

const submitLabel = computed(() => {
  if (props.isSubmitting) return 'Processing...'
  return 'Print & Submit'
})

const focusField = (field) => {
  if (! field.value) return
  field.value.focus()
}

const focusMobile = () => focusField(mobileField)
const focusSerial = () => focusField(serialField)
const focusDiscountPercent = () => focusField(discountPercentField)
const focusDiscountAmount = () => focusField(discountAmountField)
const focusPointsRedeemed = () => focusField(pointsRedeemedField)
const focusPaidAmount = () => focusField(paidAmountField)
const focusNoteReceived = () => focusField(noteReceivedField)

const openCustomerAddModal = () => {
  $store.state.isModalOpenTwo = true
}

const onCreateProfile = (profileInfo) => {
  customer.value = profileInfo
  customer.value.name = profileInfo.full_name
}

const onPaymentInput = () => {
  paymentTouched.value = true
}

const validatePaymentAmount = () => {
  const amount = Number(formData.value.payment) || 0
  if (amount < 0) {
    formData.value.payment = 0
    return showError('Payment Amount cannot be less than 0')
  }
  if (amount > netPayable.value) {
    formData.value.payment = netPayable.value
    showError('Payment Amount cannot be more than net payable')
  }
}

const isPaymentValid = () => {
  const amount = Number(formData.value.payment) || 0
  if (amount < 0) {
    showError('Paid amount cannot be less than 0')
    return false
  }
  if (amount > netPayable.value) {
    focusPaidAmount()
    showError('Paid amount cannot exceed net payable')
    return false
  }
  return true
}

const searchContact = async () => {
  if (! mobileNo.value && ! serialNo.value) {
    showError('Enter Mobile No or Serial No first')
    return
  }

  let query = companyQuery + '&roles=customer'
  if (mobileNo.value) query += `&q=${mobileNo.value}`
  if (serialNo.value) query += `&serial_no=${serialNo.value}`

  try {
    isLoader.value = true
    const res = await fetchCompanyAllContacts(query)

    if (! res.status) {
      showError('No customer found')
      return
    }

    if (res.data && res.data.data.length) {
      customer.value = {
        id: res.data.data[0].id,
        name: res.data.data[0].full_name
      }
      formData.value.contact_profile_id = res.data.data[0].id
    }

    mobileNo.value = null
    serialNo.value = null
  } catch (e) {
    showError('Failed to search customer')
  } finally {
    isLoader.value = false
  }
}

const getInitialContact = async () => {
  const contactQuery = companyQuery + '&type=customer&age=1&gender=1&fetch_one_only=1'
  const res = await fetchContactProfiles(contactQuery)
  if (res.data && res.data.length) {
    customer.value = res.data[0]
    selectedData.value = formData.value.contact = res.data[0]
  }
}

const resetData = async () => {
  receivedAmount.value = ''
  paymentTouched.value = false
  resetDiscounts()
  await getInitialContact()
  await nextTick()
  focusMobile()
}

watch(customer, () => {
  const patientQuery = companyQuery + '&type=customer&reward_balance=1'
  formData.value.contact_profile_id = null
  selectedData.value = null
  if (! customer.value) return
  formData.value.contact_profile_id = customer.value.id
  fetchSingleContactProfile(customer.value.id, patientQuery).then(res => {
    if (res.data) selectedData.value = formData.value.contact = res.data
  })
})

watch(() => formData.value.points_redeemed, (newValue) => {
  let val = Number(newValue) || 0

  if (val > rewardBalance.value) {
    showError('Points redeemed cannot be more than reward balance')
    val = rewardBalance.value
  }

  if (val > grandTotal.value) {
    showError('Points redeemed cannot be more than net bill amount')
    val = grandTotal.value
  }

  formData.value.points_redeemed = val
  if (val > 0 && props.rewardPointsAccountHeads.length) {
    formData.value.liability_head_id = props.rewardPointsAccountHeads[0].id
  }
})

watch(discountAmount, (newValue) => {
  if (newValue > 0 && props.salesAccountHeads.length) {
    formData.value.discount_head_id = props.salesAccountHeads[0].id
    formData.value.adjustment_amount = discountAmount.value
  }
})

watch(netPayable, () => {
  if (paymentTouched.value) return
  formData.value.payment = netPayable.value
})

watch(mobileNo, (val) => {
  if (val) serialNo.value = null
})

watch(serialNo, (val) => {
  if (val) mobileNo.value = null
})

onMounted(async () => {
  await getInitialContact()
  formData.value.payment = netPayable.value
})

defineExpose({
  resetData,
  isPaymentValid
})
</script>

<style scoped>
.pos-rail {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rail-block {
  background: #ffffff;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
}
.rail-head {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #667085;
  margin-bottom: 8px;
}
.rail-head i {
  color: #98a2b3;
  font-size: 11px;
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  color: #475467;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f2f4f7;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  color: #475467;
}
.chip strong {
  color: #101828;
}
.chip-points {
  background: #fef4e6;
  color: #93531c;
}
.chip-points i {
  font-size: 10px;
  color: #dc8a1f;
}
.chip-points strong {
  color: #93531c;
}
.amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;
  font-size: 13px;
  color: #475467;
}
.amount-row .val {
  font-weight: 600;
  color: #101828;
  font-variant-numeric: tabular-nums;
}
.rail-input {
  max-width: 110px;
  text-align: right;
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.point-input {
  background: #fffbe6;
  border-color: #f2d675;
}
.net-block {
  background: linear-gradient(135deg, #0a4d8c, #1a75d2);
  border-color: #0a4d8c;
  box-shadow: 0 4px 12px rgba(11, 83, 148, 0.25);
}
.net-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  color: #ffffff;
  font-weight: 700;
}
.net-row span:first-child {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.85;
}
.net-val {
  font-size: 30px;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.net-block--due {
  background: linear-gradient(135deg, #b54708, #dc6803);
  border-color: #b54708;
  box-shadow: 0 4px 12px rgba(181, 71, 8, 0.25);
}
.net-block--settled {
  background: linear-gradient(135deg, #067647, #079455);
  border-color: #067647;
  box-shadow: 0 4px 12px rgba(6, 118, 71, 0.25);
}
.net-block--change {
  background: linear-gradient(135deg, #067647, #12b76a);
  border-color: #067647;
  box-shadow: 0 4px 12px rgba(6, 118, 71, 0.28);
}
.net-status {
  margin-top: 6px;
}
.net-status-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 999px;
  padding: 2px 10px;
  font-variant-numeric: tabular-nums;
}
.paid-input {
  border-color: #1a75d2;
  font-weight: 600;
}
.quick-cash {
  display: flex;
  gap: 6px;
  padding: 4px 0;
}
.quick-btn {
  flex: 1;
  padding: 3px 0;
  font-size: 12px;
  font-weight: 600;
  color: #175cd3;
  background: #eff4ff;
  border: 1px solid #d6e0ff;
  border-radius: 8px;
}
.quick-btn:hover {
  background: #d6e4ff;
  color: #175cd3;
}
.change-row {
  border-top: 1px dashed #e5e9f0;
  margin-top: 4px;
  padding-top: 8px;
}
.change-val {
  font-weight: 700;
  font-size: 16px;
  color: #b42318;
  font-variant-numeric: tabular-nums;
}
.submit-btn {
  background: linear-gradient(135deg, #079455, #067647);
  border: none;
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(6, 118, 71, 0.3);
  transition: transform 0.1s ease, box-shadow 0.15s ease;
}
.submit-btn:hover:not(:disabled) {
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(6, 118, 71, 0.4);
  transform: translateY(-1px);
}
.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}
.rail-actions {
  display: flex;
  gap: 6px;
}
.rail-actions .btn {
  flex: 1;
  border-radius: 8px;
}
.gap-1 {
  gap: 4px;
}
</style>
